import React, { useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { RootState } from '../../redux/store';

interface ProtectedRouteProps {
  children: React.ReactNode;
  requiredRole?: 'admin' | 'reseller';
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children, requiredRole }) => {
  const location = useLocation();
  const { isAuthenticated, user } = useSelector((state: RootState) => state.user);
  const [isCheckingAuth, setIsCheckingAuth] = useState(true);
  const [redirectPath, setRedirectPath] = useState<string | null>(null);

  // Check if current path is a password reset path (these should be accessible without authentication)
  const isPasswordResetPath =
    location.pathname.startsWith('/forgot-password') ||
    location.pathname.startsWith('/reset-password');

  // Check if current path is the login page
  const isLoginPath = location.pathname === '/login';

  useEffect(() => {
    // If non-auth path like password reset, allow access
    if (isPasswordResetPath) {
      console.log('Non-auth path access granted:', location.pathname);
      setIsCheckingAuth(false);
      return;
    }

    // For all other paths, check authentication
    if (!isAuthenticated || !user) {
      // Don't redirect to login if already on login page (prevents loops)
      if (!isLoginPath) {
        console.log('Authentication required, redirecting to login');
        setRedirectPath('/login');
      }
      setIsCheckingAuth(false);
      return;
    }

    // Extra validation from localStorage in case Redux state is inconsistent
    const token = localStorage.getItem('token');
    if (!token) {
      // Don't redirect to login if already on login page (prevents loops)
      if (!isLoginPath) {
        console.log('No token found, redirecting to login');
        setRedirectPath('/login');
      }
      setIsCheckingAuth(false);
      return;
    }

    console.log(`Protected route check - Required role: ${requiredRole}, User role: ${user.role}`);

    // Role-based access control
    if (requiredRole) {
      // For admin routes, strictly check for admin role
      if (requiredRole === 'admin') {
        if (user.role !== 'admin') {
          console.log('Non-admin user attempted to access admin route. Redirecting to home.');
          setRedirectPath('/');
          setIsCheckingAuth(false);
          return;
        }
      }

      // For reseller routes, strictly check for reseller role
      if (requiredRole === 'reseller') {
        if (user.role !== 'reseller') {
          console.log(
            'Non-reseller user attempted to access reseller route. Redirecting to admin.'
          );
          setRedirectPath('/admin');
          setIsCheckingAuth(false);
          return;
        }
      }
    }

    setIsCheckingAuth(false);
  }, [isAuthenticated, user, requiredRole, location.pathname, isPasswordResetPath, isLoginPath]);

  // Show children while checking auth to prevent flicker
  if (isCheckingAuth) {
    return <>{children}</>;
  }

  // Redirect if needed
  if (redirectPath) {
    return <Navigate to={redirectPath} state={{ from: location }} replace />;
  }

  // All checks passed, render children
  return <>{children}</>;
};

export default ProtectedRoute;
